import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, RESERVATION_TYPE_LABELS, todayISO, daysBetween } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export default function ReservationDialog({ open, onOpenChange, assets, defaultDate, onSaved, editing = null, defaultType = "private" }) {
  const { user } = useAuth();
  const t = useT();
  const [form, setForm] = useState(() => editing ? {
    asset_id: editing.asset_id,
    reservation_type: editing.reservation_type,
    owner_id: editing.owner_id || "",
    start_date: editing.start_date,
    end_date: editing.end_date,
    is_protected_private_term: !!editing.is_protected_private_term,
    client_name: editing.client_name || "",
    client_contact: editing.client_contact || "",
    passenger_count: editing.passenger_count ?? "",
    trip_price: editing.trip_price ?? "",
    notes: editing.notes || "",
  } : {
    asset_id: "",
    reservation_type: defaultType,
    owner_id: user?.role === "owner_director" ? user.id : "",
    start_date: defaultDate || todayISO(),
    end_date: defaultDate || todayISO(),
    is_protected_private_term: false,
    client_name: "",
    client_contact: "",
    passenger_count: "",
    trip_price: "",
    notes: "",
  });
  const [directors, setDirectors] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/users");
      setDirectors(data.filter(u => u.role === "owner_director" || u.role === "super_admin"));
      if (assets.length && !form.asset_id) setForm(f => ({ ...f, asset_id: assets[0].id }));
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      if (!form.asset_id || !form.start_date || !form.end_date) return setConflicts([]);
      try {
        const { data } = await api.get("/reservations", {
          params: { asset_id: form.asset_id, start: form.start_date, end: form.end_date },
        });
        setConflicts(data.filter(r => r.status !== "cancelled" && r.status !== "rejected" && r.id !== editing?.id));
      } catch {}
    })();
  }, [form.asset_id, form.start_date, form.end_date, editing]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const days = form.start_date && form.end_date ? daysBetween(form.start_date, form.end_date) : 0;
  const isCommercial = form.reservation_type === "charter_skippered" || form.reservation_type === "excursion";
  const isPrivate = form.reservation_type === "private";
  const isEdit = !!editing;

  const submit = async () => {
    if (form.end_date < form.start_date) { toast.error("End date must be after start date"); return; }
    setSaving(true);
    try {
      const body = { ...form };
      if (body.passenger_count === "") delete body.passenger_count;
      if (body.trip_price === "") delete body.trip_price;
      if (!body.owner_id) delete body.owner_id;
      body.passenger_count = body.passenger_count ? parseInt(body.passenger_count) : null;
      body.trip_price = body.trip_price ? parseFloat(body.trip_price) : null;
      if (isEdit) {
        await api.patch(`/reservations/${editing.id}`, body);
      } else {
        await api.post("/reservations", body);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{isEdit ? t("resDialog.edit") : t("resDialog.new")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("resDialog.type")}</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {Object.keys(RESERVATION_TYPE_LABELS).map((k) => (
                <button
                  type="button" key={k}
                  data-testid={`res-type-${k}`}
                  onClick={() => update("reservation_type", k)}
                  className={`px-3 py-2 rounded-md border text-xs font-medium transition-colors ${form.reservation_type === k ? "bg-[#007AFF]/15 border-[#007AFF]/50 text-[#F8FAFC]" : "bg-[#050A14] border-[#1E293B] text-[#94A3B8] hover:border-[#334155]"}`}
                >{t(`reservation.${k}`)}</button>
              ))}
            </div>
          </div>

          <Field label={t("resDialog.asset")}>
            <select value={form.asset_id} onChange={(e) => update("asset_id", e.target.value)}
              data-testid="res-asset-select"
              className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
              {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("resDialog.startDate")}>
              <Input type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} data-testid="res-start-date" className="bg-[#050A14] border-[#1E293B]" />
            </Field>
            <Field label={t("resDialog.endDate")}>
              <Input type="date" value={form.end_date} onChange={(e) => update("end_date", e.target.value)} data-testid="res-end-date" className="bg-[#050A14] border-[#1E293B]" />
            </Field>
          </div>

          {days > 0 && <div className="font-mono text-[11px] text-[#94A3B8]">{t("resDialog.duration", { days })}</div>}

          {isPrivate && (
            <>
              <Field label={t("resDialog.owner")}>
                <select value={form.owner_id} onChange={(e) => update("owner_id", e.target.value)}
                  data-testid="res-owner-select"
                  className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
                  <option value="">{t("resDialog.selectOwner")}</option>
                  {directors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <div className="flex items-center justify-between bg-[#050A14] border border-[#1E293B] rounded-md p-3">
                <div>
                  <div className="text-sm font-medium">{t("resDialog.markProtected")}</div>
                  <div className="text-[11px] text-[#94A3B8]">{t("resDialog.protectedHint")}</div>
                </div>
                <Switch
                  checked={form.is_protected_private_term}
                  onCheckedChange={(v) => update("is_protected_private_term", v)}
                  data-testid="res-protected-switch"
                />
              </div>
              {form.is_protected_private_term && days < 14 && (
                <div className="flex items-start gap-2 text-[11px] text-[#FBBF24] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> {t("resDialog.fourteenWarning")}
                </div>
              )}
            </>
          )}

          {isCommercial && (
            <>
              <Field label={t("resDialog.clientName")}>
                <Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} data-testid="res-client-name" className="bg-[#050A14] border-[#1E293B]" />
              </Field>
              <Field label={t("resDialog.clientContact")}>
                <Input value={form.client_contact} onChange={(e) => update("client_contact", e.target.value)} data-testid="res-client-contact" className="bg-[#050A14] border-[#1E293B]" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("resDialog.passengers")}>
                  <Input type="number" value={form.passenger_count} onChange={(e) => update("passenger_count", e.target.value)} data-testid="res-passengers" className="bg-[#050A14] border-[#1E293B]" />
                </Field>
                <Field label={t("resDialog.priceEur")}>
                  <Input type="number" step="0.01" value={form.trip_price} onChange={(e) => update("trip_price", e.target.value)} data-testid="res-price" className="bg-[#050A14] border-[#1E293B]" />
                </Field>
              </div>
              <div className="flex items-start gap-2 text-[11px] text-[#FBBF24] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-2">
                <AlertTriangle className="w-4 h-4 shrink-0" /> {t("resDialog.commercialWarning")}
              </div>
            </>
          )}

          <Field label={t("common.notes")}>
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder={t("resDialog.notesPlaceholder")} data-testid="res-notes" className="bg-[#050A14] border-[#1E293B]" />
          </Field>

          {conflicts.length > 0 && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3 text-[11px]">
              <div className="font-mono tracking-widest text-[#F87171] mb-1">{t("resDialog.conflicts", { count: conflicts.length })}</div>
              {conflicts.map(c => (
                <div key={c.id} className="text-[#F8FAFC]">
                  {t(`reservation.${c.reservation_type}`)} · {c.start_date} → {c.end_date} ({t(`status.${c.status}`)})
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="res-cancel-btn" className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="res-submit-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            {saving ? t("common.saving") : (isEdit ? t("common.saveChanges") : t("resDialog.savedReservation"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{label.toUpperCase()}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
